import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  TrendingUp,
  RefreshCw,
  Sparkles,
  ShieldCheck,
  Bell,
  ArrowRight,
  AtSign,
  Bot,
  Globe2,
  CheckCircle2,
  LineChart as LineChartIcon,
} from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import { EMBER_LOGO } from "@/components/brand/Logo";
import Seo from "@/components/seo/Seo";
import { useLocalePath } from "@/i18n/LanguageSync";

const SHOT_BASE = "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/";
const HERO_SHOT = `${SHOT_BASE}ikby14f6_Screenshot%202.webp`;
const SHOTS = [
  { src: HERO_SHOT, testid: "ember-shot-home" },
  { src: `${SHOT_BASE}pj91wmmn_Screenshot%203.webp`, testid: "ember-shot-ai" },
  { src: `${SHOT_BASE}xp9ku9dp_Screenshot%204.webp`, testid: "ember-shot-accounts" },
  { src: `${SHOT_BASE}kp5bhalq_Screenshot%205.webp`, testid: "ember-shot-markets" },
];
const INCLUDED_ICONS = [Banknote, ShieldCheck, Bot, Sparkles, LineChartIcon, AtSign, Globe2];
const HERO_CHECK_ICONS = [ShieldCheck, Sparkles];

export default function Ember360() {
  const { t } = useTranslation("products");
  const lp = useLocalePath();
  const obj = (k) => t(k, { returnObjects: true });
  const FEATURES = [
    { icon: Banknote, title: t("ember.features.unifiedTitle"), body: t("ember.features.unifiedBody") },
    { icon: TrendingUp, title: t("ember.features.portfolioTitle"), body: t("ember.features.portfolioBody") },
    { icon: RefreshCw, title: t("ember.features.subsTitle"), body: t("ember.features.subsBody") },
    { icon: Sparkles, title: t("ember.features.aiTitle"), body: t("ember.features.aiBody") },
    { icon: Bell, title: t("ember.features.cashflowTitle"), body: t("ember.features.cashflowBody") },
    { icon: ShieldCheck, title: t("ember.features.securityTitle"), body: t("ember.features.securityBody") },
  ];
  const shotText = obj("ember.page.shots.items");
  return (
    <>
      <Seo
        title={t("ember.seoTitle")}
        description={t("ember.seoDescription")}
        path="/ember"
        preloadImages={[HERO_SHOT]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "MobileApplication",
            "name": "Ember360",
            "operatingSystem": "iOS, Android",
            "applicationCategory": "FinanceApplication",
            "url": "https://atlasforgefinancial.com/ember",
            "downloadUrl": [
              "https://apps.apple.com/us/app/ember360/id6796043696",
              "https://play.google.com/store/apps/details?id=com.atlasforge.ember360",
            ],
            "installUrl": "https://apps.apple.com/us/app/ember360/id6796043696",
            "publisher": { "@type": "Organization", "name": "AtlasForge Financial" },
            "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Product",
            "name": "Ember360 Pro",
            "description":
              "Ember360 Pro subscription — unlimited Ember AI, Insights Hub, and Plaid-linked accounts.",
            "brand": { "@type": "Brand", "name": "Ember360" },
            "offers": {
              "@type": "AggregateOffer",
              "priceCurrency": "USD",
              "lowPrice": "9.99",
              "highPrice": "89.99",
              "offerCount": 2,
              "availability": "https://schema.org/InStock",
            },
          },
        ]}
      />
      <section className="relative overflow-hidden bg-[#0A1128] text-white">
        <div className="absolute inset-0 af-ember-glow pointer-events-none opacity-60" />
        <div className="absolute inset-0 af-grid-bg opacity-[0.04]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-24 md:pt-28">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <img
                src={EMBER_LOGO}
                alt="Ember360"
                data-testid="ember-product-logo"
                width="480"
                height="240"
                decoding="async"
                fetchPriority="high"
                className="h-52 md:h-64 lg:h-72 w-auto object-contain mb-6"
                style={{ mixBlendMode: "screen" }}
              />
              <span
                data-testid="ember-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-[#FF5A36]/40 bg-[#FF5A36]/10 px-3 py-1.5 text-[12px] font-semibold text-[#FF8E73]"
              >
                <span className="h-2 w-2 rounded-full bg-[#FF5A36] af-pulse-dot" style={{ color: "#FF5A36" }} />
                {t("ember.eyebrow")}
              </span>
              <h1
                data-testid="ember-hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[72px] leading-[1.02]"
              >
                {t("ember.heroTitle")}
              </h1>
              <p className="mt-6 text-[17px] leading-relaxed text-slate-300 max-w-xl">
                {t("ember.heroBody")}
              </p>

              <div className="mt-9">
                <StoreBadges product="ember-hero" />
              </div>

              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] text-slate-400">
                {obj("ember.page.heroChecks").map((c, i) => {
                  const Icon = HERO_CHECK_ICONS[i];
                  return (
                    <span key={c} className="inline-flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      {c}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Hero phone — real app screenshot */}
            <div className="lg:col-span-5 relative">
              <div className="mx-auto max-w-[340px]">
                <div
                  data-testid="ember-hero-phone"
                  className="relative rounded-[42px] bg-gradient-to-br from-white/10 to-white/[0.02] p-1.5 shadow-2xl ring-1 ring-white/10 backdrop-blur"
                >
                  <img
                    src={HERO_SHOT}
                    alt={t("ember.page.heroAlt")}
                    fetchPriority="high"
                    decoding="async"
                    width="340"
                    height="737"
                    className="rounded-[36px] w-full block"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("ember.page.features.eyebrow")}</p>
            <h2 data-testid="ember-features-title" className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              {t("ember.page.features.title")}
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-2xl border border-slate-200 p-7 hover:-translate-y-1 transition-transform af-shadow-card bg-white">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-[#FFF0ED]">
                  <Icon className="h-5 w-5 text-[#E03C1A]" />
                </span>
                <h3 className="font-display mt-5 text-[19px]">{title}</h3>
                <p className="mt-3 text-[14px] text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* See it in action — real app screenshots */}
      <section id="screenshots" className="relative bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("ember.page.shots.eyebrow")}</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              {t("ember.page.shots.title")}
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-2xl">
              {t("ember.page.shots.body")}
            </p>
          </div>

          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {SHOTS.map((s, i) => (
              <figure key={s.testid} data-testid={s.testid} className="group">
                <div className="relative rounded-[28px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1.5 shadow-2xl ring-1 ring-black/10 group-hover:-translate-y-1 transition-transform">
                  <img
                    src={s.src}
                    alt={`${t("ember.page.shots.altPrefix")}${shotText[i].title}`}
                    loading="lazy"
                    decoding="async"
                    className="rounded-[22px] w-full block"
                  />
                </div>
                <figcaption className="mt-4">
                  <p className="font-display text-[15px] text-[#0A1128]">{shotText[i].title}</p>
                  <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{shotText[i].body}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Plans & pricing */}
      <section id="pricing" className="relative bg-[#F7F9FC] border-y border-slate-200/70">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("ember.page.pricing.eyebrow")}</p>
            <h2 data-testid="ember-pricing-title" className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              {t("ember.page.pricing.title")}
            </h2>
            <p className="mt-5 text-[15px] text-slate-600 leading-relaxed">{t("ember.page.pricing.subtitle")}</p>
          </div>

          {/* Pro plan card */}
          <div className="mt-14 grid max-w-lg mx-auto">
            <div
              data-testid="ember-plan-pro"
              className="relative rounded-2xl border-2 border-[#FF5A36] bg-white p-8 shadow-xl ring-2 ring-[#FF5A36]/10"
            >
              <span className="absolute -top-2.5 left-6 rounded-full bg-[#FF5A36] text-white text-[10.5px] font-semibold uppercase tracking-wider px-2.5 py-0.5">
                Ember360 Pro
              </span>
              <div className="inline-flex items-center justify-center h-10 w-10 rounded-xl bg-[#0A1128] text-white">
                <Sparkles className="h-5 w-5" />
              </div>
              <h3 className="mt-4 font-display text-[26px] text-[#0A1128]">{t("ember.page.pricing.cardTitle")}</h3>
              <p className="mt-1 text-[13.5px] text-slate-600">{t("ember.page.pricing.cardTagline")}</p>

              <div className="mt-6 flex items-baseline gap-2">
                <p className="font-display text-[46px] leading-none text-[#0A1128]">$9.99</p>
                <p className="text-[13.5px] text-slate-500">{t("ember.page.pricing.perMonth")}</p>
              </div>
              <p className="mt-2 text-[13.5px] text-slate-600">
                {t("ember.page.pricing.or")} <strong className="text-[#0A1128]">$89.99</strong> {t("ember.page.pricing.perYear")}{" "}
                <span className="text-[#E03C1A] font-semibold">({t("ember.page.pricing.yearlySave")})</span>
              </p>
              <p className="text-[11.5px] text-slate-400">{t("ember.page.pricing.breakEven")}</p>

              <ul className="mt-6 space-y-2.5">
                {obj("ember.page.pricing.perks").map((perk) => (
                  <li key={perk} className="flex items-start gap-2 text-[13.5px] text-slate-700 leading-snug">
                    <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#FF5A36] shrink-0" />
                    <span>{perk}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-7 flex justify-center">
                <StoreBadges product="ember-plan-pro" align="center" />
              </div>
              <p data-testid="ember-plan-note" className="mt-4 text-center text-[11.5px] text-slate-400">
                {t("ember.page.pricing.note")}
              </p>
            </div>
          </div>

          {/* Pro features shared list */}
          <div data-testid="ember-included-features" className="mt-16 max-w-5xl mx-auto rounded-3xl border border-slate-200 bg-white p-8 md:p-10">
            <div className="text-center max-w-xl mx-auto">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("ember.page.included.eyebrow")}</p>
              <h3 className="font-display mt-2 text-[26px] md:text-[32px] text-[#0A1128] leading-tight">
                {t("ember.page.included.title")}
              </h3>
            </div>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {obj("ember.page.included.items").map((f, i) => {
                const Icon = INCLUDED_ICONS[i];
                return (
                  <div key={f.title} className="flex items-start gap-3">
                    <div className="inline-flex items-center justify-center h-8 w-8 rounded-lg bg-[#FFF0ED] text-[#E03C1A] shrink-0">
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-display text-[15px] text-[#0A1128]">{f.title}</p>
                      <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed">{f.body}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="rounded-3xl bg-[#0A1128] text-white p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-[#FF5A36]/30 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 data-testid="ember-cta-title" className="font-display text-[36px] md:text-[46px] leading-[1.05]">
                  {t("ember.page.cta.title")}
                </h2>
                <p className="mt-4 text-slate-300 max-w-md">{t("ember.page.cta.body")}</p>
                <Link
                  to={lp("/safe-to-spend")}
                  data-testid="ember-cta-safetospend"
                  className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#FF8E73] hover:gap-3 transition-all"
                >
                  {t("ember.page.cta.link")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
              <div>
                <StoreBadges product="ember-bottom-cta" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
